import {
  DEFAULT_CONFIG,
  HEAD_SPAWN_TIMER_MS,
  HEAD_SPAWN_ALPHA_MS,
  HEAD_SPAWN_SCALE_DELAY_MS,
  HEAD_SPAWN_SCALE_MS,
  HEAD_SPAWN_MARKER_FALLBACK_MS,
  POWERUP_SPAWN_ANIM_MS,
  POWERUP_RADIUS,
  EFFECT_RING_SEGMENTS,
  EFFECT_RING_DISTANCE_STEP,
  EFFECT_RING_DISTANCE_OFFSET,
  EFFECT_RING_LINE_WIDTH,
  EFFECT_RING_MAX_STACKS,
  EFFECT_RING_SPEED_COLOR,
  EFFECT_RING_SPEED_GLOW,
  PU_TYPES,
  PLAYER_FLAG_ALIVE,
  PLAYER_FLAG_SPEED,
  PLAYER_FLAG_LOCAL,
  PLAYER_FLAG_DRAWING_TRAIL,
  POWERUP_FLAG_CLEAR,
  POWERUP_FLAG_GEM,
  POWERUP_POS_STRIDE
} from '../render-constants.js';

const SETTINGS_STORAGE_KEY = 'curvegen.settings.v1';
const QUERY_PARAMS = (() => {
  try {
    return new URLSearchParams(window.location.search);
  } catch (_) {
    return null;
  }
})();

function queryParam(name) {
  if (!QUERY_PARAMS) return null;
  return QUERY_PARAMS.get(name);
}

function parseBooleanLike(raw, fallback) {
  if (raw === undefined || raw === null) return fallback;
  const value = String(raw).trim().toLowerCase();
  if (!value) return fallback;
  if (value === '1' || value === 'true' || value === 'on' || value === 'yes') return true;
  if (value === '0' || value === 'false' || value === 'off' || value === 'no') return false;
  return fallback;
}

function loadPersistedSettingsSeed() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

const persistedSettingsSeed = loadPersistedSettingsSeed();

function resolveQueryBooleanPreference(paramName, fallback) {
  const fromParam = parseBooleanLike(queryParam(paramName), null);
  if (typeof fromParam === 'boolean') return fromParam;
  return fallback;
}

const INTERROUND_COUNTDOWN_START = 5;
const INTERROUND_COUNTDOWN_MS = INTERROUND_COUNTDOWN_START * 1000;
const START_SFX_LEAD_MS = 18;
const START_SFX_EARLY_WINDOW_MS = 6;
const START_SFX_TIMER_LOOKAHEAD_MS = 32;
const WEB_AUDIO_START_ADVANCE_SEC = 0.001;
const SFX_POOL_DEFAULT = 4;
const SNAPSHOT_BUFFER_LIMIT = 32;
const SNAPSHOT_BUFFER_RETENTION_MS = 1000;
const SNAPSHOT_RENDER_DELAY_MIN_MS = 0;
// Remote players (bots and human opponents alike) are sampled behind the
// newest snapshot by roughly one snapshot interval plus measured arrival
// jitter, so the renderer interpolates between two known poses instead of
// extrapolating ahead and snapping when the real pose lands. The local player
// is drawn from prediction, so this delay does not touch steering feel.
const SNAPSHOT_RENDER_DELAY_MAX_MS = 70;
const SNAPSHOT_INTERP_BASE_MARGIN_MS = 3;
const SNAPSHOT_INTERP_JITTER_MULT = 2.5;
// The delay target moves when the snapshot cadence or jitter changes; slewing
// the applied value keeps the sampled timeline from warping (which would read
// as remote snakes speeding up / slowing down).
const SNAPSHOT_RENDER_DELAY_SLEW_MS_PER_SEC = 20;
// When a late packet still outruns the interpolation buffer, the sampled pose
// jumps once the real data lands. Instead of snapping, the jump is folded
// into a per-player display offset that decays over TAU — the head glides
// back onto the true path. Offsets beyond the caps snap (teleports, resyncs).
const REMOTE_DISPLAY_SMOOTH_TAU_MS = 100;
const REMOTE_DISPLAY_FOLD_MIN_PX = 1.5;
const REMOTE_DISPLAY_FOLD_SPEED_MULT = 1.75;
const REMOTE_DISPLAY_FOLD_MIN_RAD = 0.15;
const REMOTE_DISPLAY_MAX_OFFSET_PX = 28;
const REMOTE_DISPLAY_MAX_OFFSET_RAD = 0.6;
const SNAPSHOT_EXTRAP_MAX_MS = 12;
// Adaptive extrapolation (ported from the CurveCrash smoothing overlay):
// remote players keep moving through the whole measured snapshot interval
// (x LEAD headroom) instead of stalling at the fixed floor above, following
// their measured arc. The hard cap bounds runaway on a stalled connection.
const SNAPSHOT_EXTRAP_LEAD = 1.15;
const SNAPSHOT_EXTRAP_HARD_MAX_MS = 120;
const MOTION_MIN_SPEED_PX_PER_MS = 0.004;
const MOTION_MAX_COMMIT_GAP_MS = 400;
const MOTION_MAX_OMEGA_RAD_PER_MS = 0.008;
const ACTIVE_INPUT_HEARTBEAT_MS = 24;
const IDLE_INPUT_HEARTBEAT_MS = 84;
const PING_INTERVAL_MS = 1000;
const PING_OUTSTANDING_LIMIT = 16;
const PING_SAMPLE_MAX_AGE_MS = 10000;
const LOCAL_CORRECTION_SMOOTH_MS = 80;
const LOCAL_CORRECTION_MAX_PX = 50;
const LOCAL_CORRECTION_MAX_RAD = 0.6;
const CONTROL_RESPONSIVE_WINDOW_MS = 240;
const TURN_RELEASE_GRACE_MS = 0;
const LOCAL_HEAD_TRAIL_ATTACH_MAX_PX = 1.6;
// Spatial budget for the locally predicted head while steering. At the
// fastest current powerup speed, 240 ms of prediction is about 43 px; 50 px
// leaves enough room for server-step rounding without allowing runaway lead.
const LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX = 50;
const INPUT_RETRY_BASE_MS = 14;
const INPUT_RETRY_MAX_ATTEMPTS = 4;
const INPUT_RETRY_MAX_WINDOW_MS = 240;
// Chrome and Edge timestamp key events before main-thread queueing. Recover up
// to two 60 Hz frames of that delay, while bounding the historical replay so a
// long browser stall cannot make a turn or release feel artificially heavy.
const INPUT_EVENT_DISPATCH_BACKDATE_MAX_MS = 34;
const CHAT_MAX_LINES = 80;
const DEFAULT_POWERUP_ICON_SRCS = [
  'assets/icons/green_speed.svg',
  'assets/icons/eraser.svg',
  'assets/icons/gem.svg'
];
// Speed-adaptive turn lead, from the CurveCrash steering overlay: at 2x speed a
// fixed timing error costs twice the distance, so turns are biased earlier in
// proportion to the speed multiplier. TURN_LEAD_PER_SPEED_MS is the lead per
// +1.0 of multiplier; the cap bounds it if speedPowerupMaxStacks ever rises
// above 1. Set TURN_LEAD_PER_SPEED_MS to 0 to disable -- see the asymmetry
// warning on speedAdaptiveTurnLeadMs in input/input.js before tuning it.
const TURN_LEAD_PER_SPEED_MS = 25;
const TURN_LEAD_MAX_MS = 25;
const ACTIVE_PREDICTION_LEAD_MAX_MS = 240;
// Floor for the idle lead only. The value actually used is measured from the
// snapshot cadence and the link (see idlePredictionLeadMs); this constant is
// what it falls back to before either has been observed.
const IDLE_PREDICTION_LEAD_MAX_MS = 32;
// Headroom on top of interval + one-way + jitter, so an ordinary late packet
// does not immediately re-expose the freeze the idle lead exists to prevent.
const IDLE_PREDICTION_LEAD_MARGIN_MS = 12;
const ENABLE_LOCAL_PREDICTION = true;
const USE_AUTHORITATIVE_SNAPSHOTS_ONLY = false;
const LOCAL_INPUT_APPLY_EPSILON_MS = 1;


const SFX = {
  click: '/assets/sounds/web/click.mp3',
  countdown: '/assets/sounds/web/countdown.wav',
  start: '/assets/sounds/web/round-start.mp3',
  death: '/assets/sounds/web/death.mp3',
  powerAppear: '/assets/sounds/web/power-appear.mp3',
  powerGrab: '/assets/sounds/web/power-grab.mp3',
  ready: '/assets/sounds/web/ready.mp3',
  roundEnd: '/assets/sounds/web/round-end.mp3',
  win: '/assets/sounds/web/win.mp3'
};

export {
  DEFAULT_CONFIG,
  HEAD_SPAWN_TIMER_MS,
  HEAD_SPAWN_ALPHA_MS,
  HEAD_SPAWN_SCALE_DELAY_MS,
  HEAD_SPAWN_SCALE_MS,
  HEAD_SPAWN_MARKER_FALLBACK_MS,
  POWERUP_SPAWN_ANIM_MS,
  POWERUP_RADIUS,
  EFFECT_RING_SEGMENTS,
  EFFECT_RING_DISTANCE_STEP,
  EFFECT_RING_DISTANCE_OFFSET,
  EFFECT_RING_LINE_WIDTH,
  EFFECT_RING_MAX_STACKS,
  EFFECT_RING_SPEED_COLOR,
  EFFECT_RING_SPEED_GLOW,
  PU_TYPES,
  PLAYER_FLAG_ALIVE,
  PLAYER_FLAG_SPEED,
  PLAYER_FLAG_LOCAL,
  PLAYER_FLAG_DRAWING_TRAIL,
  POWERUP_FLAG_CLEAR,
  POWERUP_FLAG_GEM,
  POWERUP_POS_STRIDE,
  SETTINGS_STORAGE_KEY,
  QUERY_PARAMS,
  queryParam,
  parseBooleanLike,
  loadPersistedSettingsSeed,
  persistedSettingsSeed,
  resolveQueryBooleanPreference,
  INTERROUND_COUNTDOWN_START,
  INTERROUND_COUNTDOWN_MS,
  START_SFX_LEAD_MS,
  START_SFX_EARLY_WINDOW_MS,
  START_SFX_TIMER_LOOKAHEAD_MS,
  WEB_AUDIO_START_ADVANCE_SEC,
  SFX_POOL_DEFAULT,
  SNAPSHOT_BUFFER_LIMIT,
  SNAPSHOT_BUFFER_RETENTION_MS,
  SNAPSHOT_RENDER_DELAY_MIN_MS,
  SNAPSHOT_RENDER_DELAY_MAX_MS,
  SNAPSHOT_INTERP_BASE_MARGIN_MS,
  SNAPSHOT_INTERP_JITTER_MULT,
  SNAPSHOT_RENDER_DELAY_SLEW_MS_PER_SEC,
  REMOTE_DISPLAY_SMOOTH_TAU_MS,
  REMOTE_DISPLAY_FOLD_MIN_PX,
  REMOTE_DISPLAY_FOLD_SPEED_MULT,
  REMOTE_DISPLAY_FOLD_MIN_RAD,
  REMOTE_DISPLAY_MAX_OFFSET_PX,
  REMOTE_DISPLAY_MAX_OFFSET_RAD,
  SNAPSHOT_EXTRAP_MAX_MS,
  SNAPSHOT_EXTRAP_LEAD,
  SNAPSHOT_EXTRAP_HARD_MAX_MS,
  MOTION_MIN_SPEED_PX_PER_MS,
  MOTION_MAX_COMMIT_GAP_MS,
  MOTION_MAX_OMEGA_RAD_PER_MS,
  ACTIVE_INPUT_HEARTBEAT_MS,
  IDLE_INPUT_HEARTBEAT_MS,
  PING_INTERVAL_MS,
  PING_OUTSTANDING_LIMIT,
  PING_SAMPLE_MAX_AGE_MS,
  LOCAL_CORRECTION_SMOOTH_MS,
  LOCAL_CORRECTION_MAX_PX,
  LOCAL_CORRECTION_MAX_RAD,
  CONTROL_RESPONSIVE_WINDOW_MS,
  TURN_RELEASE_GRACE_MS,
  LOCAL_HEAD_TRAIL_ATTACH_MAX_PX,
  LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX,
  INPUT_RETRY_BASE_MS,
  INPUT_RETRY_MAX_ATTEMPTS,
  INPUT_RETRY_MAX_WINDOW_MS,
  INPUT_EVENT_DISPATCH_BACKDATE_MAX_MS,
  CHAT_MAX_LINES,

  DEFAULT_POWERUP_ICON_SRCS,
  TURN_LEAD_PER_SPEED_MS,
  TURN_LEAD_MAX_MS,
  ACTIVE_PREDICTION_LEAD_MAX_MS,
  IDLE_PREDICTION_LEAD_MAX_MS,
  IDLE_PREDICTION_LEAD_MARGIN_MS,
  ENABLE_LOCAL_PREDICTION,
  USE_AUTHORITATIVE_SNAPSHOTS_ONLY,
  LOCAL_INPUT_APPLY_EPSILON_MS,
  SFX
};
